import axios from 'axios'
import { jwtDecode } from '../utils'
import mockResponse from '../testing/mock-responses'
import debug from '../services/debug'

const endpointUrl =
  window.env === 'prod'
    ? 'https://loans.bankrate.com'
    : 'https://loans.qa.bankrate.com'
const apiKey = window.loansApiKey

/**
 * getUserData - make request to personal loans api to grab user data to pre-populate the form
 * @param {Object} payload
 */
const getUserData = async ({ authData, fake }) => {
  // if testing flag 'fake' is being used,
  // return mock response
  if (fake) {
    return mockResponse({ type: 'getUserData' })
  }

  // Get payload ready
  let url = `${endpointUrl}/v1/customers`
  const authHeader = `Bearer ${authData.token}`
  const query = JSON.stringify({
    query: `query {
            getProfile {
              first_name
              last_name
              email_address
              street_address
              home_phone
              city
              state
              zipcode
            }
          }`,
    variables: {}
  })

  // Grab data
  let res = null
  try {
    let prepopResponse = await axios.post(url, query, {
      headers: { Authorization: authHeader }
    })
    // Authentication errors get passed back as 200s, need to check for existance of res data
    if (!prepopResponse.data.data) {
      throw 'fetch-prepop-data-failure'
    }

    let parsedResponse = prepopResponse.data.data.getProfile
    parsedResponse = {
      firstName: parsedResponse.first_name ? parsedResponse.first_name : '',
      lastName: parsedResponse.last_name ? parsedResponse.last_name : '',
      streetAddress: parsedResponse.street_address
        ? parsedResponse.street_address
        : '',
      city: parsedResponse.city ? parsedResponse.city : '',
      state: parsedResponse.state ? parsedResponse.state : '',
      zipCode: parsedResponse.zipcode ? parsedResponse.zipcode : '',
      emailAddress: parsedResponse.email_address
        ? parsedResponse.email_address
        : '',
      phoneNumber: parsedResponse.home_phone ? parsedResponse.home_phone : ''
    }

    res = parsedResponse
  } catch (error) {
    // Log to console on debug mode
    debug.log(
      '%cError:',
      'background: #eb4034; color: #fff; padding: 3px; font-weight: 700;',
      `${error}`
    )
  }

  return res
}

/**
 * createUser - makes request to personal loans api, creates new Bankrate user
 * @param {Object} payload
 */
const createUser = async ({ createAccountParams, fake, error }) => {
  // if we're forcing an error for testing...
  if (error) {
    throw 'User request error test'
  }

  // if testing flag 'fake' is being used,
  // return mock response
  if (fake) {
    return mockResponse({ type: 'createUser' })
  }

  let res = null
  let url = `${endpointUrl}/v1/user`

  // 10 second timeout
  let timeout = 10000

  let response = await axios({
    url: url,
    method: 'post',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': apiKey
    },
    data: createAccountParams,
    timeout: timeout,
    responseType: 'json'
  })

  let accessToken = response.data.Body.token
  let customerId = jwtDecode(accessToken).customer_id

  res = {
    accessToken,
    customerId
  }

  return res
}

/**
 * updateProfile - makes request to personal loans api after user creation, updates newly created user profile with data from form
 * @param {Object} payload
 */
const updateProfile = async ({
  updateProfileParams,
  isLoggedIn,
  fake,
  error
}) => {
  // if we're forcing an error for testing...
  if (error) {
    throw 'User request error test'
  }

  // if testing flag 'fake' is being used,
  // return mock response
  if (fake) {
    return mockResponse({
      type: 'updateProfile'
    })
  }

  // If somehow we didn't get an access token back for the new user...
  if (!isLoggedIn) {
    return
  }

  let url = `${endpointUrl}/v1/customers`

  // 10 second timeout
  let timeout = 10000

  // graphql query for which fields to update
  const query = JSON.stringify({
    query: `mutation {
      updateProfile (input: {
        ${
          updateProfileParams.firstName
            ? `first_name: "${updateProfileParams.firstName}",`
            : ''
        }
        ${
          updateProfileParams.lastName
            ? `last_name: "${updateProfileParams.lastName}",`
            : ''
        }
        ${
          updateProfileParams.emailAddress
            ? `email_address: "${updateProfileParams.emailAddress}",`
            : ''
        }
        ${
          updateProfileParams.streetAddress
            ? `street_address: "${updateProfileParams.streetAddress}",`
            : ''
        }
        ${
          updateProfileParams.city ? `city: "${updateProfileParams.city}",` : ''
        }
        ${
          updateProfileParams.state
            ? `state: "${updateProfileParams.state}",`
            : ''
        }
        ${
          updateProfileParams.zipCode
            ? `zipcode: "${updateProfileParams.zipCode}",`
            : ''
        }
        ${
          updateProfileParams.phoneNumber
            ? `home_phone: "${updateProfileParams.phoneNumber}",`
            : ''
        }
      })
    }`
  })

  // Make req to user update endpoint
  let res = await axios({
    url: url,
    method: 'post',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': apiKey,
      Authorization: `Bearer ${updateProfileParams.accessToken}`
    },
    data: query,
    timeout: timeout,
    responseType: 'json'
  })

  return res
}

/**
 * createPasswordlessUser - if account creation fails, we try to create a password-less user using only the email address
 * @param {Object} payload
 */
const createPasswordlessUser = async ({
  createPasswordlessAccountParams,
  fake,
  error
}) => {
  // if we're forcing an error for testing...
  if (error) {
    throw 'User request error test'
  }

  // if testing flag 'fake' is being used,
  // return mock response
  if (fake) {
    return mockResponse({ type: 'createPasswordlessUser' })
  }

  let res = null
  let url = `${endpointUrl}/v1/passwordless-user`

  // 10 second timeout
  let timeout = 10000

  let response = await axios({
    url: url,
    method: 'post',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': apiKey
    },
    data: createPasswordlessAccountParams,
    timeout: timeout,
    responseType: 'json'
  })

  let customerId = response.data.Body.customer_id

  res = { customerId }

  return res
}

export { getUserData, createUser, updateProfile, createPasswordlessUser }
